import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';

// Icons
import HowToRegIcon from '@mui/icons-material/HowToReg';
import EditIcon from '@mui/icons-material/Edit';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import HotelIcon from '@mui/icons-material/Hotel';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Store
import { useAppStore } from '../../stores';

// Styles
import './Home.scss';

interface StepData {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  linkText?: string;
  linkPath?: string;
  linkUrl?: string;
  isLast?: boolean;
}

interface HomeStepProps {
  step: StepData;
  onLinkClick?: (path: string) => void;
}

const HomeStep: React.FC<HomeStepProps> = ({ step, onLinkClick }) => {
  const handleLinkClick = () => {
    if (step.linkPath && onLinkClick) {
      onLinkClick(step.linkPath);
    }
  };

  return (
    <div className={`home-step ${step.isLast ? 'home-step--last' : ''}`}>
      <div className="home-step__indicator">
        <div className={`home-step__number ${step.isLast ? 'home-step__number--complete' : ''}`}>
          {step.isLast ? (
            <CheckCircleIcon className="home-step__check-icon" />
          ) : (
            step.number
          )}
        </div>
        {!step.isLast && <div className="home-step__connector" />}
      </div>

      <div className="home-step__content">
        <div className="home-step__header">
          <span className="home-step__icon">{step.icon}</span>
          <Typography className="home-step__title" component="h3">
            {step.title}
          </Typography>
        </div>

        {step.description && (
          <Typography className="home-step__description">
            {step.description}
          </Typography>
        )}

        {step.linkText && step.linkPath && (
          <div className="home-step__link-wrapper">
            <a
              href="#"
              className="home-step__link"
              onClick={(e) => {
                e.preventDefault();
                handleLinkClick();
              }}
            >
              {step.linkText}
              <ChevronRightIcon className="home-step__link-arrow" />
            </a>
          </div>
        )}

        {step.linkText && step.linkUrl && (
          <div className="home-step__link-wrapper">
            <a
              href={step.linkUrl}
              className="home-step__link"
              target="_blank"
              rel="noreferrer"
            >
              {step.linkText}
              <ChevronRightIcon className="home-step__link-arrow" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const Home: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:767px)');

  // Get user info from store
  const userRecord = useAppStore((state) => state.user.user) as { firstName?: string } | null;
  const userName = userRecord?.firstName || 'User';
  const steps: StepData[] = [
    {
      number: 1,
      icon: <EditIcon />,
      title: 'Complete your delegate certification',
      description: 'The delegate certification must be completed by the President before delegates can be reported.',
      linkText: 'Delegate certification',
      linkPath: '/delegate-certification',
    },
    {
      number: 2,
      icon: <PersonSearchIcon />,
      title: 'Report delegates and alternates',
      description: 'Use the member search to find members, register them or update their credential type if they self registered.',
      linkText: 'Search for members',
      linkPath: '/convention-registration/search',
    },
    {
      number: 3,
      icon: <HotelIcon />,
      title: 'Make your hotel reservations',
      description: 'Check your confirmation email for instructions to finalize your registration and make your hotel reservation.',
      linkText: 'Housing information',
      linkUrl: 'https://www.aft.org/convention/housing-information',
    },
    {
      number: 4,
      icon: <HowToRegIcon />,
      title: "Don't forget to register yourself",
      description: 'Remember to search for your own member record and register yourself for the convention.',
      linkText: 'Find your member record',
      linkPath: '/convention-registration/search',
    },
    {
      number: 5,
      icon: <FactCheckIcon />,
      title: 'View your compliance',
      description: 'Confirm that all of the details look correct by viewing your compliance.',
      linkText: 'See compliance',
      linkPath: '/affiliate-info',
    },
    {
      number: 6,
      icon: <CheckCircleIcon />,
      title: 'Check your email for updates leading up to July!',
      description: '',
      isLast: true,
    },
  ];

  const handleLinkClick = (path: string) => {
    window.location.href = path;
  };

  return (
    <Box className="home-page">
      <div className="home-header">
        <Typography
          variant="h1"
          className="home-welcome-title"
          component="h1"
        >
          Welcome back, {userName}!
        </Typography>
        <Typography className="home-welcome-subtitle">
          Make sure you complete the following steps in order to successfully register you and your delegates.
        </Typography>
      </div>

      <div className="home-stepper">
        {steps.map((step) => (
          <HomeStep
            key={step.number}
            step={step}
            onLinkClick={handleLinkClick}
          />
        ))}
      </div>
    </Box>
  );
};

export default Home;
